/* eslint-disable class-methods-use-this */
import React from 'react';
import { wrapComponent } from 'utils/framework';
import BaseClass from 'components/BaseClass';
import {
    Container, Box, Text, Grid, Button, Divider
} from 'components/ui';
import { fontWeights, space } from 'style/config';
import Banner from 'components/Content/Banner';
import Copy from 'components/Content/Copy';
import contentConstants from 'constants/content';
import userUtils from 'utils/User';
import Markdown from 'components/Markdown/Markdown';
import rwdAdvocacyPageBindings from 'analytics/bindingMethods/pages/rwdAdvocacy/rwdAdvocacyPageBindings';
import { HEADER_VALUE } from 'constants/authentication';

const { BANNER_TYPES } = contentConstants;
const { setPageLoadAnaytics } = rwdAdvocacyPageBindings;

// Intro line ("To receive your discount:") stays bold; the numbered steps can be authored bold in
// Contentful, so force them to normal per design. Extracted to a module-level constant so it isn't
// rebuilt on every render (Emotion memoization).
const referralInstructionsStyle = {
    css: {
        '& > *:first-child': { fontWeight: fontWeights.bold },
        '& ol, & ol *': { fontWeight: `${fontWeights.normal} !important` },
        '& ol': { listStyle: 'none', paddingLeft: 0, counterReset: 'advocacyStep' },
        '& ol > li': {
            position: 'relative',
            counterIncrement: 'advocacyStep',
            paddingLeft: space[5],
            '&::before': {
                content: 'counter(advocacyStep) "."',
                position: 'absolute',
                left: 0,
                top: 0
            }
        },
        '& ol > li + li': { marginTop: space[2] }
    }
};

// OPT-23062: Fixed max width for the Landing action buttons (matches Figma).
const ACTION_COLUMN_MAX_WIDTH = 340;
class AdvocacyLanding extends BaseClass {
    componentDidMount() {
        setPageLoadAnaytics(this.props.pageType);
    }

    renderJoinBI = ({ actionParams }) => {
        return (
            <Button
                variant='primary'
                data-at={Sephora.debug.dataAt('join_now_btn')}
                onClick={() =>
                    this.props.showBiRegisterModal({
                        ...actionParams,
                        callback: () => {
                            // Notify parent component of BI conversion success in order to show proper success
                            // barcode page for non bi users converting to BI user
                            this.props.onBiConversionSuccess();
                        }
                    })
                }
                children={this.props.locales.joinNow}
            />
        );
    };

    renderRegister = ({ variant = 'primary', actionParams, block }) => {
        return (
            <Button
                variant={variant}
                {...(block && { width: '100%' })}
                data-at={Sephora.debug.dataAt('create_account_btn')}
                onClick={() => this.props.showRegisterModal(actionParams)}
                children={this.props.locales.createAccount}
            />
        );
    };

    renderSignIn = ({ actionParams, block }) => {
        return (
            <Button
                variant='primary'
                {...(block && { width: '100%' })}
                data-at={Sephora.debug.dataAt('sign_in_btn')}
                onClick={() =>
                    this.props.showSignInModal({
                        ...actionParams,
                        extraParams: { ...actionParams.extraParams, headerValue: HEADER_VALUE.USER_CLICK }
                    })
                }
                children={this.props.locales.signIn}
            />
        );
    };

    renderActionButtons = ({ referral, isBI, stacked }) => {
        const actionParams = {
            isOpen: true,
            extraParams: { referral }
        };

        if (!userUtils.isAnonymous()) {
            if (!isBI) {
                return this.renderJoinBI({ actionParams });
            }
        } else {
            if (stacked) {
                return (
                    <>
                        {this.renderSignIn({
                            block: true,
                            actionParams
                        })}
                        <Box marginTop={3}>
                            {this.renderRegister({
                                variant: 'secondary',
                                block: true,
                                actionParams
                            })}
                        </Box>
                    </>
                );
            }

            return (
                <Grid columns={[0, 2]}>
                    {this.renderSignIn({
                        block: true,
                        actionParams
                    })}
                    {this.renderRegister({
                        variant: 'secondary',
                        block: true,
                        actionParams
                    })}
                </Grid>
            );
        }

        return null;
    };

    render() {
        const {
            seo,
            globalHeroBanner,
            globalInvitationText,
            referralOfferDescription,
            referralInstructions,
            referralDisclaimer,
            referrerFirstName,
            referral,
            isEnhanced
        } = this.props;

        if (isEnhanced) {
            return (
                <Container
                    paddingX={[0, 4]}
                    paddingBottom={0}
                >
                    <Box
                        paddingX={[4, 0]}
                        paddingTop={[0, 6]}
                    >
                        <Grid
                            columns={[0, '2fr 3fr']}
                            gap={0}
                            alignItems='start'
                        >
                            <Box>
                                {globalHeroBanner?.[0]?.media?.src && (
                                    <Banner
                                        media={globalHeroBanner[0].media}
                                        largeMedia={globalHeroBanner[0].largeMedia}
                                        bannerType={BANNER_TYPES.HERO}
                                        seoHeader={seo?.header1}
                                        marginTop={0}
                                        marginBottom={[5, 0]}
                                        alignLeft
                                        enablePageRenderTracking={true}
                                    />
                                )}
                            </Box>
                            <Box marginTop={[5, 0]}>
                                <Box marginLeft={[0, 7]}>
                                    <Text
                                        is='p'
                                        data-at={Sephora.debug.dataAt('referrer_text')}
                                        children={`${globalInvitationText} ${referrerFirstName}`}
                                    />
                                    <Markdown
                                        fontWeight='bold'
                                        fontSize='lg'
                                        marginTop={2}
                                        data-at={Sephora.debug.dataAt('offer_description')}
                                        content={referralOfferDescription}
                                    />
                                    {referralInstructions && (
                                        <Copy
                                            content={referralInstructions}
                                            marginTop={5}
                                            data-at={Sephora.debug.dataAt('instructions')}
                                            marginBottom={5}
                                            style={referralInstructionsStyle}
                                        />
                                    )}
                                    <Box maxWidth={['100%', ACTION_COLUMN_MAX_WIDTH]}>
                                        {this.renderActionButtons({
                                            isBI: userUtils.isBI(),
                                            referral,
                                            stacked: true
                                        })}
                                    </Box>
                                </Box>
                            </Box>
                        </Grid>
                        {referralDisclaimer && (
                            <Grid
                                columns={[0, '2fr 3fr']}
                                gap={0}
                            >
                                <Box />
                                <Box>
                                    <Box marginLeft={[0, 7]}>
                                        <Divider
                                            marginTop={[6, 7]}
                                            marginBottom={[5, 4]}
                                        />
                                        <Box
                                            fontSize='sm'
                                            color='gray'
                                        >
                                            <Copy
                                                content={referralDisclaimer}
                                                data-at={Sephora.debug.dataAt('offer_disclaimer')}
                                                marginTop={0}
                                                marginBottom={0}
                                            />
                                        </Box>
                                    </Box>
                                </Box>
                            </Grid>
                        )}
                    </Box>
                </Container>
            );
        }

        return (
            <Container
                paddingX={[0, 4]}
                paddingBottom={0}
            >
                {globalHeroBanner?.[0] && (
                    <Banner
                        {...globalHeroBanner[0]}
                        bannerType={BANNER_TYPES.HERO}
                        seoHeader={seo?.header1}
                        marginTop={[0, 5]}
                        marginBottom={[6, 7]}
                        alignLeft
                        enablePageRenderTracking={true}
                    />
                )}
                <Box paddingX={[4, 0]}>
                    <Grid
                        columns={[0, 2]}
                        gap={0}
                    >
                        <Box paddingRight={[0, 1]}>
                            <Text
                                is='p'
                                data-at={Sephora.debug.dataAt('referrer_text')}
                                children={`${globalInvitationText} ${referrerFirstName}`}
                            />
                            <Markdown
                                fontWeight='bold'
                                fontSize='2xl'
                                marginTop={5}
                                data-at={Sephora.debug.dataAt('offer_description')}
                                content={referralOfferDescription}
                            />
                        </Box>
                        <Box
                            borderLeft={[0, '1px solid lightGray']}
                            marginTop={[5, 0]}
                        >
                            <Box marginLeft={[0, 7]}>
                                {referralInstructions && (
                                    <Copy
                                        content={referralInstructions}
                                        marginTop={0}
                                        data-at={Sephora.debug.dataAt('instructions')}
                                        marginBottom={5}
                                    />
                                )}
                                {this.renderActionButtons({
                                    isBI: userUtils.isBI(),
                                    referral
                                })}
                            </Box>
                        </Box>
                    </Grid>
                    {referralDisclaimer && (
                        <>
                            <Divider
                                marginTop={[6, 7]}
                                marginBottom={[5, 4]}
                            />
                            <Copy
                                content={referralDisclaimer}
                                data-at={Sephora.debug.dataAt('offer_disclaimer')}
                                marginTop={0}
                                marginBottom={0}
                            />
                        </>
                    )}
                </Box>
            </Container>
        );
    }
}

export default wrapComponent(AdvocacyLanding, 'AdvocacyLanding', true);
